import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchLayaways,
  fetchLayaway,
  fetchSalesHistory,
  updateDebtorProfile,
  createLayaway,
  addLayawayPayment,
  updateLayawayPayment,
  cancelLayaway,
  fetchItems,
  fetchInventory,
  fetchUsers,
  fetchBranches,
  fetchSettings,
} from '@/api';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { APP_NAME, pageTitle } from '@/lib/appConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ShoppingCart,
  Search,
  Plus,
  Eye,
  DollarSign,
  Calendar,
  User,
  Package,
  Trash2,
  Ban,
  Minus,
  Filter,
  Grid3X3,
  List,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Sparkles,
  CreditCard,
  Banknote,
  Smartphone,
  Building2,
  Settings,
  FileText,
  Printer,
} from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { debounce } from 'lodash';
import { CreateLayawayTab } from '@/components/layaways/CreateLayawayTab';
import { LayawayInvoice } from '@/components/layaways/LayawayInvoice';
import { EditLayawayDialog } from '@/components/layaways/EditLayawayDialog';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { Edit } from 'lucide-react';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

interface Layaway {
  id: string;
  layaway_number: number;
  customer_name: string;
  customer_phone?: string;
  customer_address?: string;
  total_amount: number;
  paid_amount: number;
  remaining_amount: number;
  status: 'active' | 'completed' | 'cancelled';
  product_handling: 'hold' | 'handover';
  payment_schedule_months: number;
  created_at: string;
  days_active?: number;
  balance_due?: number;
  items?: LayawayItem[];
  payments?: LayawayPayment[];
}

interface LayawayItem {
  id: string;
  item_id: string;
  name: string;
  sku: string;
  quantity: number;
  price: number;
}

interface LayawayPayment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  notes?: string;
}

interface Item {
  id: string;
  name: string;
  sku: string;
  selling_price: string | number;
  quantity: number;
  barcode?: string;
  vat_enabled?: number;
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  buying_price?: number;
  quantity: number;
  sku: string;
  vat_enabled?: number;
  barcode?: string;
}

interface DebtorProfile {
  id: string;
  name: string;
  phone?: string;
  address?: string;
}

interface SalesHistoryRow {
  id: string;
  invoice_number: string | number;
  record_type?: 'sale' | 'layaway_payment';
  total_amount: number;
  created_at: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  payment_method?: string;
}

const Layaways: React.FC = () => {
  useEffect(() => {
    document.title = pageTitle('Debtors');
  }, []);
  const [layaways, setLayaways] = useState<Layaway[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  });
  const [activeTab, setActiveTab] = useState('debtors');
  const [profileSearch, setProfileSearch] = useState('');
  // The Debtors tab groups ALL layaways by customer, so it needs the full
  // dataset rather than whichever single page the Credit Sales tab's own
  // server-side pagination happens to have loaded.
  const [allDebtorLayaways, setAllDebtorLayaways] = useState<Layaway[]>([]);
  const [debtorPage, setDebtorPage] = useState(1);
  const DEBTOR_PAGE_SIZE = 10;

  // Create Debtor states
  const [items, setItems] = useState<Item[]>([]);
  const [inventory, setInventory] = useState<any[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customer, setCustomer] = useState({
    name: '',
    phone: '',
    address: '',
  });
  const [debtorProfiles, setDebtorProfiles] = useState<DebtorProfile[]>([]);
  const [productHandling, setProductHandling] = useState<'hold' | 'handover'>('hold');
  const [paymentScheduleMonths, setPaymentScheduleMonths] = useState(1);
  const [initialPayment, setInitialPayment] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [vatPercentage, setVatPercentage] = useState<number>(0);
  const [systemTimezone, setSystemTimezone] = useState<string>('Africa/Windhoek');
  // Current date in system timezone (not UTC)
  const [layawayDate, setLayawayDate] = useState<Date>(() => {
    const now = toZonedTime(new Date(), 'Africa/Windhoek');
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const currencySymbol = useCurrencySymbol();
  const [companySettings, setCompanySettings] = useState({
    company_name: APP_NAME,
    address: '123 Business Street',
    phone: '+1234567890',
    email: ''
  });

  // Enhanced UI states
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemViewMode, setItemViewMode] = useState<'grid' | 'list'>('grid');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [isCreating, setIsCreating] = useState(false);
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [hasUnsavedCreateSale, setHasUnsavedCreateSale] = useState(false);

  // View/Payment Dialog states
  const [selectedLayaway, setSelectedLayaway] = useState<Layaway | null>(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingLayaway, setEditingLayaway] = useState<Layaway | null>(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileCustomer, setProfileCustomer] = useState<{ name: string; phone?: string; address?: string } | null>(null);
  const [profileLayaways, setProfileLayaways] = useState<Layaway[]>([]);
  const [profilePurchases, setProfilePurchases] = useState<SalesHistoryRow[]>([]);
  const [showEditCustomerDialog, setShowEditCustomerDialog] = useState(false);
  const [isSavingCustomerEdit, setIsSavingCustomerEdit] = useState(false);
  const [editingCustomerProfile, setEditingCustomerProfile] = useState<{
    current_name: string;
    current_phone?: string;
    name: string;
    phone: string;
    address: string;
  } | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethodDialog, setPaymentMethodDialog] = useState('cash');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(formatInTimeZone(new Date(), 'Africa/Windhoek', 'yyyy-MM-dd'));
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const [editingPayment, setEditingPayment] = useState<LayawayPayment | null>(null);
  const [editPaymentAmount, setEditPaymentAmount] = useState('');
  const [editPaymentMethod, setEditPaymentMethod] = useState('cash');
  const { confirmNavigation, confirmationDialog } = useUnsavedChangesGuard({
    when: hasUnsavedCreateSale && activeTab === 'create' && !isCreating,
  });
  const [editPaymentDate, setEditPaymentDate] = useState(formatInTimeZone(new Date(), 'Africa/Windhoek', 'yyyy-MM-dd'));
  const [editPaymentNotes, setEditPaymentNotes] = useState('');
  const getSystemDateString = useCallback((date: Date = new Date()) => {
    return formatInTimeZone(date, systemTimezone, 'yyyy-MM-dd');
  }, [systemTimezone]);
  const getSystemDateTime = useCallback((date: string) => {
    return `${date} 00:00:00`;
  }, []);

  // Branch selection
  const [branches, setBranches] = useState<any[]>([]);
  const [selectedBranch, setSelectedBranch] = useState<string>('');

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Calculate available quantities for items based on selected branch
  const itemsWithAvailability = useMemo(() => {
    return items.map(item => {
      // Get inventory records for this item in the selected branch
      const branchInventory = inventory.filter(inv => 
        inv.item_id === item.id && 
        (selectedBranch ? inv.branch_id === selectedBranch : true)
      );
      
      // Calculate total available quantity (using available_quantity if present, otherwise quantity)
      const totalAvailable = branchInventory.reduce((sum, inv) => {
        const available = inv.available_quantity !== undefined ? inv.available_quantity : inv.quantity;
        return sum + parseInt(available || 0, 10);
      }, 0);
      
      return {
        ...item,
        availableQuantity: totalAvailable,
        originalQuantity: item.quantity
      };
    });
  }, [items, inventory, selectedBranch]);

  // Enhanced item filtering and sorting
  const filteredAndSortedItems = useMemo(() => {
    let filtered = itemsWithAvailability;

    // Search filter
    if (itemSearchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        item.sku.toLowerCase().includes(itemSearchTerm.toLowerCase()) ||
        (item.barcode && item.barcode.includes(itemSearchTerm))
      );
    }

    // Sort items
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return parseFloat(a.selling_price.toString()) - parseFloat(b.selling_price.toString());
        case 'stock':
          return b.availableQuantity - a.availableQuantity;
        default:
          return 0;
      }
    });

    return filtered;
  }, [itemsWithAvailability, itemSearchTerm, categoryFilter, sortBy]);

  const cartSubtotal = useMemo(() => {
    return cart.reduce((total, item) => total + (parseFloat(item.price.toString()) * item.quantity), 0);
  }, [cart]);

  const cartVAT = useMemo(() => {
    return cart.reduce((total, item) => {
      if (item.vat_enabled) {
        const itemTotal = parseFloat(item.price.toString()) * item.quantity;
        return total + (itemTotal * (vatPercentage / 100));
      }
      return total;
    }, 0);
  }, [cart, vatPercentage]);

  const cartTotal = useMemo(() => {
    return cartSubtotal + cartVAT;
  }, [cartSubtotal, cartVAT]);

  const remainingBalance = useMemo(() => {
    return Math.max(0, cartTotal - initialPayment);
  }, [cartTotal, initialPayment]);

  const monthlyPayment = useMemo(() => {
    return paymentScheduleMonths > 0 ? remainingBalance / paymentScheduleMonths : 0;
  }, [remainingBalance, paymentScheduleMonths]);

  const customerOptions = useMemo<DebtorProfile[]>(() => {
    const map = new Map<string, DebtorProfile>();

    debtorProfiles.forEach((profile) => {
      const key = `${profile.name.trim().toLowerCase()}|${(profile.phone || '').trim().toLowerCase()}`;
      if (!map.has(key) && profile.name.trim()) {
        map.set(key, profile);
      }
    });

    allDebtorLayaways.forEach((layaway) => {
      const key = `${layaway.customer_name.trim().toLowerCase()}|${(layaway.customer_phone || '').trim().toLowerCase()}`;
      if (!map.has(key) && layaway.customer_name.trim()) {
        map.set(key, {
          id: `layaway-${layaway.id}`,
          name: layaway.customer_name,
          phone: layaway.customer_phone || '',
          address: layaway.customer_address || '',
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [debtorProfiles, allDebtorLayaways]);

  const debtorProfileRows = useMemo(() => {
    const grouped = new Map<string, {
      name: string;
      phone?: string;
      address?: string;
      layaways: Layaway[];
      totalCredit: number;
      totalPaid: number;
      outstanding: number;
      lastActivity: string;
    }>();

    allDebtorLayaways.forEach((entry) => {
      const key = `${(entry.customer_name || '').trim().toLowerCase()}|${(entry.customer_phone || '').trim().toLowerCase()}`;
      if (!key || !entry.customer_name) return;

      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, {
          name: entry.customer_name,
          phone: entry.customer_phone,
          address: entry.customer_address,
          layaways: [entry],
          totalCredit: Number(entry.total_amount || 0),
          totalPaid: Number(entry.paid_amount || 0),
          outstanding: Number(entry.remaining_amount || 0),
          lastActivity: entry.created_at,
        });
        return;
      }

      existing.layaways.push(entry);
      existing.totalCredit += Number(entry.total_amount || 0);
      existing.totalPaid += Number(entry.paid_amount || 0);
      existing.outstanding += Number(entry.remaining_amount || 0);
      if (new Date(entry.created_at).getTime() > new Date(existing.lastActivity).getTime()) {
        existing.lastActivity = entry.created_at;
      }
    });

    const q = profileSearch.trim().toLowerCase();
    return Array.from(grouped.values())
      .filter((row) => {
        if (!q) return true;
        return (
          row.name.toLowerCase().includes(q) ||
          (row.phone || '').toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDebtorLayaways, profileSearch]);

  const debtorLastPage = Math.max(1, Math.ceil(debtorProfileRows.length / DEBTOR_PAGE_SIZE));

  useEffect(() => {
    setDebtorPage(1);
  }, [profileSearch]);

  const paginatedDebtorRows = useMemo(() => {
    const start = (debtorPage - 1) * DEBTOR_PAGE_SIZE;
    return debtorProfileRows.slice(start, start + DEBTOR_PAGE_SIZE);
  }, [debtorProfileRows, debtorPage]);

  const formatLayawayDate = useCallback((date: string | number | Date) => {
    if (!date) return 'N/A';

    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }

    const normalizedDate =
      typeof date === 'string' && !date.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(date)
        ? `${date}Z`
        : date;
    const parsed = new Date(normalizedDate);

    if (Number.isNaN(parsed.getTime())) {
      return String(date);
    }

    return formatInTimeZone(parsed, systemTimezone, 'yyyy-MM-dd');
  }, [systemTimezone]);

  const fetchLayawaysData = useCallback(
    debounce(async () => {
      try {
        setLoading(true);
        const response = await fetchLayaways({
          page: pagination.current_page,
          per_page: pagination.per_page,
          search: searchTerm,
          status: statusFilter,
        });
        setLayaways(response.data.data);
        setPagination(response.data.pagination);
      } catch (error) {
        console.error('Failed to fetch debtors:', error);
        toast.error('Failed to fetch debtors');
      } finally {
        setLoading(false);
      }
    }, 300),
    [pagination.current_page, pagination.per_page, searchTerm, statusFilter]
  );

  useEffect(() => {
    fetchLayawaysData();
  }, [fetchLayawaysData]);

  const fetchAllDebtorLayaways = useCallback(async () => {
    try {
      const response = await fetchLayaways({ page: 1, per_page: 1000 });
      setAllDebtorLayaways(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch debtor profiles:', error);
      toast.error('Failed to fetch debtor profiles');
    }
  }, []);

  useEffect(() => {
    fetchAllDebtorLayaways();
  }, [fetchAllDebtorLayaways]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('debtor_profiles');
      if (!stored) return;
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return;
      setDebtorProfiles(parsed);
    } catch (error) {
      console.error('Failed to load debtor profiles:', error);
    }
  }, []);

  useEffect(() => {
    const loadItemsAndInventory = async () => {
      try {
        const [itemsResponse, inventoryResponse, settingsResponse] = await Promise.all([
          fetchItems(),
          fetchInventory(),
          fetchSettings()
        ]);
        setItems(itemsResponse.data);
        setInventory(inventoryResponse.data);
        setVatPercentage(settingsResponse.data.vat_percentage || 0);
        setSystemTimezone(settingsResponse.data.timezone || 'Africa/Windhoek');
        setCompanySettings({
          company_name: settingsResponse.data.company_name || APP_NAME,
          address: settingsResponse.data.address || '123 Business Street',
          phone: settingsResponse.data.phone || '+1234567890',
          email: settingsResponse.data.email || ''
        });
      } catch (error) {
        console.error('Failed to fetch items or inventory:', error);
      }
    };
    loadItemsAndInventory();
  }, []);

  // Keep debtor date aligned with system timezone changes
  useEffect(() => {
    const now = toZonedTime(new Date(), systemTimezone);
    now.setHours(0, 0, 0, 0);
    setLayawayDate(now);
    setPaymentDate(getSystemDateString(new Date()));
  }, [systemTimezone]);

  useEffect(() => {
    const loadBranches = async () => {
      try {
        const response = await fetchBranches();
        setBranches(response.data);
        
        // If not admin, set the user's branch as selected
        if (!isAdmin && user?.branch_id) {
          setSelectedBranch(user.branch_id);
        } else if (isAdmin && response.data.length > 0) {
          // For admin, select the main branch if it exists, otherwise first branch
          const mainBranch = response.data.find(b => b.isMain || b.is_main);
          setSelectedBranch(mainBranch ? mainBranch.id : response.data[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    };
    loadBranches();
  }, [isAdmin, user?.branch_id]);

  // Re-validate cart when branch changes
  useEffect(() => {
    if (!selectedBranch) return;
    
    // Validate cart items against new branch inventory
    if (cart.length > 0) {
      const updatedCart = cart.map(cartItem => {
        const itemWithAvailability = itemsWithAvailability.find(i => i.id === cartItem.id);
        const availableQty = itemWithAvailability?.availableQuantity || 0;
        
        // Adjust quantity if it exceeds available
        if (cartItem.quantity > availableQty) {
          if (availableQty === 0) {
            return null; // Mark for removal
          }
          toast.warning(`${cartItem.name} quantity adjusted from ${cartItem.quantity} to ${availableQty} (max available)`);
          return { ...cartItem, quantity: availableQty };
        }
        return cartItem;
      }).filter(Boolean); // Remove null items
      
      if (updatedCart.length !== cart.length) {
        toast.warning(`Some items were removed from cart as they're not available in the selected branch.`);
      }
      
      if (JSON.stringify(updatedCart) !== JSON.stringify(cart)) {
        setCart(updatedCart);
      }
    }
  }, [selectedBranch, itemsWithAvailability]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value === 'all' ? '' : value);
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const addToCart = (item: any, quantity = 1, unitPrice?: number, vatEnabled?: boolean) => {
    // Check if we have a selected branch
    if (!selectedBranch && isAdmin) {
      toast.error('Please select a branch first');
      return;
    }

    const requestedQty = Math.max(1, Math.floor(Number(quantity) || 1));
    
    // Get the item with availability info
    const itemWithAvailability = itemsWithAvailability.find(i => i.id === item.id);
    const availableQty = itemWithAvailability?.availableQuantity || 0;
    const requestedPrice = Number.isFinite(unitPrice as number) ? Number(unitPrice) : parseFloat(item.selling_price as any) || 0;
    const requestedVatEnabled = vatEnabled === undefined ? Boolean(item.vat_enabled) : vatEnabled;
    
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    const currentCartQty = existingItem ? existingItem.quantity : 0;

    if (currentCartQty + requestedQty > availableQty) {
      toast.error(`Cannot add more items. Only ${availableQty} available in selected branch.`);
      return;
    }

    if (existingItem) {
      setCart((prev) => prev.map(cartItem =>
        cartItem.id === item.id
          ? {
              ...cartItem,
              quantity: cartItem.quantity + requestedQty,
              price: requestedPrice,
              vat_enabled: requestedVatEnabled ? 1 : 0,
            }
          : cartItem
      ));
    } else {
      if (availableQty > 0) {
        setCart((prev) => [...prev, {
          id: item.id,
          name: item.name,
          price: requestedPrice,
          buying_price: parseFloat(item.buying_price as any) || 0,
          quantity: requestedQty,
          sku: item.sku,
          barcode: item.barcode || '',
          vat_enabled: requestedVatEnabled ? 1 : 0,
        }]);
      } else {
        toast.error('Item is out of stock in selected branch');
      }
    }
  };

  const updateCartItemVat = (itemId: string, vatEnabled: boolean) => {
    setCart((prev) => prev.map((cartItem: any) =>
      cartItem.id === itemId
        ? { ...cartItem, vat_enabled: vatEnabled ? 1 : 0 }
        : cartItem
    ));
  };

  const updateCartItemPrice = (itemId: string, newPrice: number) => {
    setCart(cart.map(cartItem =>
      cartItem.id === itemId
        ? { ...cartItem, price: newPrice }
        : cartItem
    ));
  };

  const removeFromCart = (itemId: string) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const updateCartQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    // Check against available quantity in selected branch
    const itemWithAvailability = itemsWithAvailability.find(i => i.id === itemId);
    const availableQty = itemWithAvailability?.availableQuantity || 0;
    
    if (quantity > availableQty) {
      toast.error(`Cannot add more items. Only ${availableQty} available in selected branch.`);
      return;
    }

    setCart(cart.map(cartItem =>
      cartItem.id === itemId
        ? { ...cartItem, quantity }
        : cartItem
    ));
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + (parseFloat(item.price.toString()) * item.quantity), 0);
  };

  const handleCreateLayaway = async () => {
    if (!customer.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    if (cart.length === 0) {
      toast.error('Please add items to the debtor');
      return;
    }

    if (initialPayment > cartTotal) {
      toast.error('Initial payment cannot exceed total amount');
      return;
    }

    // Validation for required fields
    if (!user?.id) {
      toast.error('User ID is required');
      return;
    }

    // Check for branch_id - it's required by the backend
    const branchId = isAdmin ? selectedBranch : user?.branch_id;
    if (!branchId) {
      toast.error('Please select a branch');
      return;
    }

    setIsCreating(true);
    try {
      const layawayData = {
        user_id: user.id,
        branch_id: branchId,
        customer_name: customer.name.trim(),
        customer_phone: customer.phone?.trim() || null,
        customer_address: customer.address?.trim() || null,
        total_amount: parseFloat(cartTotal.toFixed(2)),
        subtotal: parseFloat(cartTotal.toFixed(2)),
        product_handling: productHandling,
        payment_schedule_months: parseInt(paymentScheduleMonths.toString()),
        initial_payment: parseFloat(initialPayment.toFixed(2)),
        payment_method: paymentMethod,
        notes: notes?.trim() || null,
        initial_payment_date: formatInTimeZone(
          layawayDate,
          systemTimezone,
          "yyyy-MM-dd HH:mm:ss"
        ),
        // Store layaway date in configured system timezone
        layaway_date: formatInTimeZone(
          layawayDate,
          systemTimezone,
          "yyyy-MM-dd HH:mm:ss"
        ),
        items: cart.map(item => ({
          item_id: item.id.toString(),
          quantity: parseInt(item.quantity.toString()),
          price: parseFloat(item.price.toString()),
        })),
      };

      console.log('Debtor data being sent:', layawayData);

      await createLayaway(layawayData);
      toast.success('🎉 Debtor created successfully!');
      
      // Reset form
      setCart([]);
      setCustomer({ name: '', phone: '', address: '' });
      setProductHandling('hold');
      setPaymentScheduleMonths(1);
      setInitialPayment(0);
      setPaymentMethod('cash');
      setNotes('');
      setItemSearchTerm('');
      // Reset to system timezone date
      const now = toZonedTime(new Date(), systemTimezone);
      now.setHours(0, 0, 0, 0);
      setLayawayDate(now);
      setActiveTab('list');
      fetchLayawaysData();
      fetchAllDebtorLayaways();
    } catch (error) {
      console.error('Failed to create debtor:', error);
      toast.error('Failed to create debtor');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateDebtorProfile = (profile: { name: string; phone: string; address: string }) => {
    const normalizedName = profile.name.trim();
    const normalizedPhone = profile.phone.trim();
    const normalizedAddress = profile.address.trim();
    if (!normalizedName) return;

    setDebtorProfiles((prev) => {
      const duplicate = prev.find((entry) =>
        entry.name.trim().toLowerCase() === normalizedName.toLowerCase() &&
        (entry.phone || '').trim().toLowerCase() === normalizedPhone.toLowerCase()
      );
      if (duplicate) {
        setCustomer({
          name: duplicate.name,
          phone: duplicate.phone || '',
          address: duplicate.address || '',
        });
        return prev;
      }

      const next = [
        ...prev,
        {
          id: `profile-${Date.now()}`,
          name: normalizedName,
          phone: normalizedPhone,
          address: normalizedAddress,
        },
      ];
      localStorage.setItem('debtor_profiles', JSON.stringify(next));
      return next;
    });
  };

  const handleViewLayaway = async (layawayId: string) => {
    try {
      const response = await fetchLayaway(layawayId);
      setSelectedLayaway(response.data);
      setShowViewDialog(true);
    } catch (error) {
      console.error('Failed to fetch debtor details:', error);
      toast.error('Failed to fetch debtor details');
    }
  };

  const handleAddPayment = async () => {
    if (!selectedLayaway || !paymentAmount) {
      toast.error('Please enter a payment amount');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (amount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }

    if (amount > selectedLayaway.remaining_amount) {
      toast.error('Payment amount cannot exceed remaining balance');
      return;
    }

    setIsAddingPayment(true);
    try {
      await addLayawayPayment(selectedLayaway.id, {
        user_id: user?.id,
        amount: amount,
        payment_method: paymentMethodDialog,
        notes: paymentNotes || null,
        payment_date: getSystemDateTime(paymentDate),
      });

      const isFullyPaid = amount >= selectedLayaway.remaining_amount;
      toast.success(
        isFullyPaid 
          ? '🎉 Payment added! Debtor is now complete!' 
          : '💰 Payment added successfully'
      );
      
      setShowPaymentDialog(false);
      setPaymentAmount('');
      setPaymentNotes('');
      setPaymentDate(getSystemDateString());
      fetchLayawaysData();
      fetchAllDebtorLayaways();
      
      if (showViewDialog) {
        handleViewLayaway(selectedLayaway.id);
      }
    } catch (error) {
      console.error('Failed to add payment:', error);
      toast.error('Failed to add payment');
    } finally {
      setIsAddingPayment(false);
    }
  };

  const openEditPaymentDialog = (payment: LayawayPayment) => {
    setEditingPayment(payment);
    setEditPaymentAmount(Number(payment.amount || 0).toString());
    setEditPaymentMethod(payment.payment_method || 'cash');
    setEditPaymentNotes(payment.notes || '');
    setEditPaymentDate(formatLayawayDate(payment.payment_date));
    setShowEditPaymentDialog(true);
  };

  const handleUpdatePayment = async () => {
    if (!selectedLayaway || !editingPayment) return;

    const amount = parseFloat(editPaymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }

    const oldAmount = Number(editingPayment.amount || 0);
    const adjustedRemaining = Number(selectedLayaway.remaining_amount || 0) + oldAmount;
    if (amount > adjustedRemaining) {
      toast.error('Payment amount cannot exceed remaining balance');
      return;
    }

    setIsUpdatingPayment(true);
    try {
      await updateLayawayPayment(selectedLayaway.id, editingPayment.id, {
        user_id: user?.id,
        amount,
        payment_method: editPaymentMethod,
        notes: editPaymentNotes || null,
        payment_date: getSystemDateTime(editPaymentDate),
      });

      const refreshed = await fetchLayaway(selectedLayaway.id);
      setSelectedLayaway(refreshed.data);
      fetchLayawaysData();
      fetchAllDebtorLayaways();
      setShowEditPaymentDialog(false);
      setEditingPayment(null);
      toast.success('Payment updated successfully');
    } catch (error) {
      console.error('Failed to update payment:', error);
      toast.error('Failed to update payment');
    } finally {
      setIsUpdatingPayment(false);
    }
  };

  const handleCancelLayaway = async (layawayId: string) => {
    if (!confirm('Are you sure you want to cancel this debtor?')) return;

    try {
      await cancelLayaway(layawayId);
      toast.success('Debtor cancelled successfully');
      fetchLayawaysData();
      fetchAllDebtorLayaways();
      setShowViewDialog(false);
    } catch (error) {
      console.error('Failed to cancel debtor:', error);
      toast.error('Failed to cancel debtor');
    }
  };

  const handleGenerateInvoice = async (layaway: Layaway) => {
    try {
      const response = await fetchLayaway(layaway.id);
      setSelectedLayaway(response.data);
      setShowInvoiceDialog(true);
    } catch (error) {
      console.error('Failed to fetch debtor details for invoice:', error);
      toast.error('Failed to fetch debtor details for invoice');
    }
  };

  const isSameCustomer = (customerName: string, customerPhone: string | undefined, rowName?: string | null, rowPhone?: string | null) => {
    const normalizedCustomerName = (customerName || '').trim().toLowerCase();
    const normalizedCustomerPhone = (customerPhone || '').trim().toLowerCase();
    const normalizedRowName = (rowName || '').trim().toLowerCase();
    const normalizedRowPhone = (rowPhone || '').trim().toLowerCase();

    if (!normalizedCustomerName || normalizedCustomerName !== normalizedRowName) {
      return false;
    }

    if (!normalizedCustomerPhone) {
      return true;
    }

    return normalizedCustomerPhone === normalizedRowPhone;
  };

  const handleViewDebtorProfile = async (layaway: Layaway) => {
    setShowProfileDialog(true);
    setIsProfileLoading(true);
    setProfileCustomer({
      name: layaway.customer_name,
      phone: layaway.customer_phone,
      address: layaway.customer_address,
    });

    try {
      const [layawaysRes, salesRes] = await Promise.all([
        fetchLayaways({
          page: 1,
          per_page: 500,
          search: layaway.customer_name || '',
        }),
        fetchSalesHistory({
          page: 1,
          per_page: 500,
          search: layaway.customer_name || '',
        }),
      ]);

      const matchedLayaways = (layawaysRes.data.data || []).filter((entry: Layaway) =>
        isSameCustomer(layaway.customer_name, layaway.customer_phone, entry.customer_name, entry.customer_phone)
      );

      const matchedPurchases = (salesRes.data.data || []).filter((entry: SalesHistoryRow) =>
        entry.record_type === 'sale' &&
        isSameCustomer(layaway.customer_name, layaway.customer_phone, entry.customer_name, entry.customer_phone)
      );

      setProfileLayaways(matchedLayaways);
      setProfilePurchases(matchedPurchases);
    } catch (error) {
      console.error('Failed to load debtor profile:', error);
      toast.error('Failed to load debtor profile');
      setProfileLayaways([]);
      setProfilePurchases([]);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleSaveCustomerProfile = async () => {
    if (!editingCustomerProfile) return;
    if (!editingCustomerProfile.name.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setIsSavingCustomerEdit(true);
    try {
      const payload = {
        current_name: editingCustomerProfile.current_name,
        current_phone: editingCustomerProfile.current_phone || '',
        name: editingCustomerProfile.name.trim(),
        phone: editingCustomerProfile.phone.trim(),
        address: editingCustomerProfile.address.trim(),
      };
      const response = await updateDebtorProfile(payload);
      const updatedCount = Number(response?.data?.updated_count || 0);
      if (updatedCount === 0) {
        toast.warning('No matching debtor profiles were updated');
      } else {
        toast.success(`Updated customer details in ${updatedCount} credit sale(s)`);
      }
      setShowEditCustomerDialog(false);
      setEditingCustomerProfile(null);
      fetchLayawaysData();
      fetchAllDebtorLayaways();
    } catch (error) {
      console.error('Failed to update customer profile:', error);
      toast.error('Failed to update customer details');
    } finally {
      setIsSavingCustomerEdit(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-blue-100 text-blue-800">Active</Badge>;
      case 'completed':
        return <Badge className="bg-green-100 text-green-800">Completed</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="mx-auto w-full max-w-[1800px] px-4 md:px-6 py-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
              <Calendar className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Debtors
              </h1>
              <p className="text-slate-600">Manage partial payment sales</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg px-4 py-2 shadow-sm border">
              <div className="flex items-center space-x-2 text-sm">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span className="text-slate-600">Active</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-slate-600">Completed</span>
                </div>
                <div className="w-px h-4 bg-slate-300"></div>
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-slate-600">Cancelled</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            const switchTab = () => {
              setActiveTab(value);
              if (value === 'debtors' && (searchTerm || statusFilter)) {
                setSearchTerm('');
                setStatusFilter('');
                setPagination((prev) => ({ ...prev, current_page: 1 }));
              }
            };

            if (activeTab === 'create' && value !== 'create') {
              confirmNavigation(switchTab);
              return;
            }

            switchTab();
          }}
          className="space-y-6"
        >
          <TabsList className="bg-white border shadow-sm">
            <TabsTrigger 
              value="debtors" 
              className="flex items-center space-x-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <User className="h-4 w-4" />
              <span>Debtors</span>
            </TabsTrigger>
            <TabsTrigger 
              value="list" 
              className="flex items-center space-x-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <List className="h-4 w-4" />
              <span>Credit Sales</span>
            </TabsTrigger>
            <TabsTrigger 
              value="create"
              className="flex items-center space-x-2 data-[state=active]:bg-blue-500 data-[state=active]:text-white"
            >
              <Plus className="h-4 w-4" />
              <span>Create Credit Sale</span>
            </TabsTrigger>
          </TabsList>

        <TabsContent value="debtors">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search debtor profiles..."
                    value={profileSearch}
                    onChange={(e) => setProfileSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Total Credit</TableHead>
                      <TableHead>Total Paid</TableHead>
                      <TableHead>Outstanding</TableHead>
                      <TableHead>Last Activity</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedDebtorRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-500">
                          No debtor profiles found
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedDebtorRows.map((profile) => (
                        <TableRow key={`${profile.name}-${profile.phone || 'na'}`}>
                          <TableCell className="font-medium">{profile.name}</TableCell>
                          <TableCell>{profile.phone || '-'}</TableCell>
                          <TableCell>{currencySymbol}{profile.totalCredit.toFixed(2)}</TableCell>
                          <TableCell>{currencySymbol}{profile.totalPaid.toFixed(2)}</TableCell>
                          <TableCell className={profile.outstanding > 0 ? 'text-orange-700 font-semibold' : ''}>
                            {currencySymbol}{profile.outstanding.toFixed(2)}
                          </TableCell>
                          <TableCell>{formatLayawayDate(profile.lastActivity)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDebtorProfile(profile.layaways[0])}
                              >
                                View Profile
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingCustomerProfile({
                                    current_name: profile.name,
                                    current_phone: profile.phone || '',
                                    name: profile.name,
                                    phone: profile.phone || '',
                                    address: profile.address || '',
                                  });
                                  setShowEditCustomerDialog(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSearchTerm(profile.name);
                                  setActiveTab('list');
                                }}
                              >
                                View Credit Sales
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {debtorLastPage > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {((debtorPage - 1) * DEBTOR_PAGE_SIZE) + 1} to{' '}
                    {Math.min(debtorPage * DEBTOR_PAGE_SIZE, debtorProfileRows.length)} of{' '}
                    {debtorProfileRows.length} debtors
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDebtorPage(1)}
                      disabled={debtorPage === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDebtorPage(prev => prev - 1)}
                      disabled={debtorPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-3 py-1 bg-gray-100 rounded">
                      Page {debtorPage} of {debtorLastPage}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDebtorPage(prev => prev + 1)}
                      disabled={debtorPage === debtorLastPage}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDebtorPage(debtorLastPage)}
                      disabled={debtorPage === debtorLastPage}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="list">
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search debtors..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={statusFilter || 'all'} onValueChange={handleStatusFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                    <TableHeader>
                      <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Total Amount</TableHead>
                      <TableHead>Paid Amount</TableHead>
                      <TableHead>Remaining</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Handling</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : layaways.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center">
                          No debtors found
                        </TableCell>
                      </TableRow>
                    ) : (
                      layaways.map((layaway) => (
                        <TableRow key={layaway.id}>
                          <TableCell className="font-medium">
                            {layaway.layaway_number}
                          </TableCell>
                          <TableCell>
                            <div>
                              <div className="font-medium">{layaway.customer_name}</div>
                              {layaway.customer_phone && (
                                <div className="text-sm text-gray-500">
                                  {layaway.customer_phone}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{currencySymbol}{layaway.total_amount.toFixed(2)}</TableCell>
                          <TableCell>{currencySymbol}{layaway.paid_amount.toFixed(2)}</TableCell>
                          <TableCell>{currencySymbol}{layaway.remaining_amount.toFixed(2)}</TableCell>
                          <TableCell>{getStatusBadge(layaway.status)}</TableCell>
                          <TableCell>
                            <Badge variant={layaway.product_handling === 'hold' ? 'secondary' : 'outline'}>
                              {layaway.product_handling === 'hold' ? 'Hold' : 'Handover'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatLayawayDate(layaway.created_at)}
                          </TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewLayaway(layaway.id)}
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDebtorProfile(layaway)}
                                title="Debtor Profile"
                              >
                                <User className="h-4 w-4" />
                              </Button>
                              {layaway.status === 'active' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    try {
                                      const response = await fetchLayaway(layaway.id);
                                      setEditingLayaway(response.data);
                                      setShowEditDialog(true);
                                    } catch (error) {
                                      console.error('Failed to fetch debtor for editing:', error);
                                      toast.error('Failed to fetch debtor details');
                                    }
                                  }}
                                  title="Edit Debtor"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleGenerateInvoice(layaway)}
                                title="Generate Invoice"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              {layaway.status === 'active' && (
                                <>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedLayaway(layaway);
                                      setShowPaymentDialog(true);
                                    }}
                                  >
                                    <DollarSign className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelLayaway(layaway.id)}
                                  >
                                    <Ban className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination Controls */}
              {pagination.last_page > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-gray-600">
                    Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} credit sales
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: 1 }))}
                      disabled={pagination.current_page === 1}
                    >
                      First
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
                      disabled={pagination.current_page === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm px-3 py-1 bg-gray-100 rounded">
                      Page {pagination.current_page} of {pagination.last_page}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
                      disabled={pagination.current_page === pagination.last_page}
                    >
                      Next
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, current_page: pagination.last_page }))}
                      disabled={pagination.current_page === pagination.last_page}
                    >
                      Last
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="create">
          <CreateLayawayTab
            filteredAndSortedItems={filteredAndSortedItems}
            itemSearchTerm={itemSearchTerm}
            setItemSearchTerm={setItemSearchTerm}
            sortBy={sortBy}
            setSortBy={setSortBy}
            cart={cart}
            addToCart={addToCart}
            updateCartItemVat={updateCartItemVat}
            removeFromCart={removeFromCart}
            updateCartQuantity={updateCartQuantity}
            updateCartItemPrice={updateCartItemPrice}
            customer={customer}
            setCustomer={setCustomer}
            customerOptions={customerOptions}
            onCreateCustomer={handleCreateDebtorProfile}
            branches={branches}
            selectedBranch={selectedBranch}
            setSelectedBranch={setSelectedBranch}
            isAdmin={isAdmin}
            productHandling={productHandling}
            setProductHandling={setProductHandling}
            paymentScheduleMonths={paymentScheduleMonths}
            setPaymentScheduleMonths={setPaymentScheduleMonths}
            initialPayment={initialPayment}
            setInitialPayment={setInitialPayment}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            notes={notes}
            setNotes={setNotes}
            systemTimezone={systemTimezone}
            layawayDate={layawayDate}
            setLayawayDate={setLayawayDate}
            cartSubtotal={cartSubtotal}
            cartVAT={cartVAT}
            cartTotal={cartTotal}
            vatPercentage={vatPercentage}
            currencySymbol={currencySymbol}
            createLayaway={handleCreateLayaway}
            isCreating={isCreating}
            onDirtyStateChange={setHasUnsavedCreateSale}
          />
        </TabsContent>
        </Tabs>
        {confirmationDialog}

        <Dialog open={showEditCustomerDialog} onOpenChange={setShowEditCustomerDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Customer Details</DialogTitle>
            </DialogHeader>
            {editingCustomerProfile ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name *</label>
                  <Input
                    value={editingCustomerProfile.name}
                    onChange={(e) => setEditingCustomerProfile((prev) => prev ? { ...prev, name: e.target.value } : prev)}
                    placeholder="Customer name"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    value={editingCustomerProfile.phone}
                    onChange={(e) => setEditingCustomerProfile((prev) => prev ? { ...prev, phone: e.target.value } : prev)}
                    placeholder="Phone number"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Address</label>
                  <Input
                    value={editingCustomerProfile.address}
                    onChange={(e) => setEditingCustomerProfile((prev) => prev ? { ...prev, address: e.target.value } : prev)}
                    placeholder="Address"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowEditCustomerDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveCustomerProfile} disabled={isSavingCustomerEdit}>
                    {isSavingCustomerEdit ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Debtor Profile Dialog */}
        <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>Debtor Profile</DialogTitle>
            </DialogHeader>
            {isProfileLoading ? (
              <div className="py-8 text-center text-gray-500">Loading profile...</div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Total Credit</div>
                      <div className="text-xl font-bold">
                        {currencySymbol}{profileLayaways.reduce((sum, entry) => sum + Number(entry.total_amount || 0), 0).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Total Paid</div>
                      <div className="text-xl font-bold text-green-700">
                        {currencySymbol}{profileLayaways.reduce((sum, entry) => sum + Number(entry.paid_amount || 0), 0).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Outstanding</div>
                      <div className="text-xl font-bold text-orange-700">
                        {currencySymbol}{profileLayaways.reduce((sum, entry) => sum + Number(entry.remaining_amount || 0), 0).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-sm text-gray-500">Purchases</div>
                      <div className="text-xl font-bold">
                        {currencySymbol}{profilePurchases.reduce((sum, entry) => sum + Number(entry.total_amount || 0), 0).toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {profileCustomer && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Customer</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm space-y-1">
                      <div><strong>Name:</strong> {profileCustomer.name}</div>
                      <div><strong>Phone:</strong> {profileCustomer.phone || '-'}</div>
                      <div><strong>Address:</strong> {profileCustomer.address || '-'}</div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Debtor Credit History</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Debtor #</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Total</TableHead>
                              <TableHead>Paid</TableHead>
                              <TableHead>Remaining</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {profileLayaways.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-gray-500">
                                  No debtor records found
                                </TableCell>
                              </TableRow>
                            ) : (
                              profileLayaways.map((entry) => (
                                <TableRow key={entry.id}>
                                  <TableCell>{entry.layaway_number}</TableCell>
                                  <TableCell>{formatLayawayDate(entry.created_at)}</TableCell>
                                  <TableCell>{currencySymbol}{Number(entry.total_amount || 0).toFixed(2)}</TableCell>
                                  <TableCell>{currencySymbol}{Number(entry.paid_amount || 0).toFixed(2)}</TableCell>
                                  <TableCell>{currencySymbol}{Number(entry.remaining_amount || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Purchase History</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Invoice</TableHead>
                              <TableHead>Date</TableHead>
                              <TableHead>Method</TableHead>
                              <TableHead>Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {profilePurchases.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-gray-500">
                                  No purchase records found
                                </TableCell>
                              </TableRow>
                            ) : (
                              profilePurchases.map((entry) => (
                                <TableRow key={entry.id}>
                                  <TableCell>{entry.invoice_number}</TableCell>
                                  <TableCell>{formatLayawayDate(entry.created_at)}</TableCell>
                                  <TableCell className="capitalize">{(entry.payment_method || 'cash').replace('_', ' ')}</TableCell>
                                  <TableCell>{currencySymbol}{Number(entry.total_amount || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* View Debtor Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                Debtor #{selectedLayaway?.layaway_number} Details
              </DialogTitle>
            </DialogHeader>
            {selectedLayaway && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <h3 className="font-semibold mb-2">Customer Information</h3>
                    <p><strong>Name:</strong> {selectedLayaway.customer_name}</p>
                    {selectedLayaway.customer_phone && (
                      <p><strong>Phone:</strong> {selectedLayaway.customer_phone}</p>
                    )}
                    {selectedLayaway.customer_address && (
                      <p><strong>Address:</strong> {selectedLayaway.customer_address}</p>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Debtor Details</h3>
                    <p><strong>Status:</strong> {getStatusBadge(selectedLayaway.status)}</p>
                    <p><strong>Product Handling:</strong> {selectedLayaway.product_handling}</p>
                    <p><strong>Payment Schedule:</strong> {selectedLayaway.payment_schedule_months} months</p>
                  <p><strong>Created:</strong> {formatLayawayDate(selectedLayaway.created_at)}</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Items</h3>
                  <div className="overflow-x-auto">
                    <Table className="min-w-[640px]">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead>SKU</TableHead>
                          <TableHead>Quantity</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedLayaway.items?.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.name}</TableCell>
                            <TableCell>{item.sku}</TableCell>
                            <TableCell>{item.quantity}</TableCell>
                            <TableCell>{currencySymbol}{parseFloat(item.price.toString()).toFixed(2)}</TableCell>
                            <TableCell>{currencySymbol}{(item.quantity * parseFloat(item.price.toString())).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 rounded bg-gray-50 p-4 sm:grid-cols-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold">{currencySymbol}{selectedLayaway.total_amount.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Total Amount</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{currencySymbol}{selectedLayaway.paid_amount.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Paid Amount</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">{currencySymbol}{selectedLayaway.remaining_amount.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">Remaining Amount</div>
                  </div>
                </div>

                {selectedLayaway.payments && selectedLayaway.payments.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Payment History</h3>
                    <div className="overflow-x-auto">
                      <Table className="min-w-[720px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedLayaway.payments.map((payment) => (
                            <TableRow key={payment.id}>
                              <TableCell>{formatLayawayDate(payment.payment_date)}</TableCell>
                              <TableCell>{currencySymbol}{payment.amount.toFixed(2)}</TableCell>
                              <TableCell className="capitalize">{payment.payment_method.replace('_', ' ')}</TableCell>
                              <TableCell>{payment.notes || '-'}</TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditPaymentDialog(payment)}
                                >
                                  <Edit className="h-4 w-4 mr-1" />
                                  Edit
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowViewDialog(false);
                      setShowInvoiceDialog(true);
                    }}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Invoice
                  </Button>
                  {selectedLayaway.status === 'active' && (
                    <>
                      <Button
                        onClick={() => {
                          setShowPaymentDialog(true);
                          setShowViewDialog(false);
                        }}
                      >
                        Add Payment
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => handleCancelLayaway(selectedLayaway.id)}
                      >
                        Cancel Debtor
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Payment Dialog - Enhanced */}
        <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
          <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg">
                  <DollarSign className="h-5 w-5 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-xl">Add Payment</DialogTitle>
                  <p className="text-sm text-slate-600 mt-1">Record a new payment for this debtor</p>
                </div>
              </div>
            </DialogHeader>
            <div className="space-y-6">
              {selectedLayaway && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Debtor #</span>
                      <span className="text-sm font-bold text-blue-600">#{selectedLayaway.layaway_number}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Customer</span>
                      <span className="text-sm text-slate-800">{selectedLayaway.customer_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Total Amount</span>
                      <span className="text-sm text-slate-800">{currencySymbol}{selectedLayaway.total_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-700">Paid Amount</span>
                      <span className="text-sm text-green-600">{currencySymbol}{selectedLayaway.paid_amount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center border-t border-blue-200 pt-2">
                      <span className="font-semibold text-slate-700">Remaining Balance</span>
                      <span className="font-bold text-lg text-orange-600">{currencySymbol}{selectedLayaway.remaining_amount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Amount</label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="pl-10 text-lg font-semibold bg-white border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    max={selectedLayaway?.remaining_amount}
                    step="0.01"
                  />
                </div>
                {paymentAmount && selectedLayaway && parseFloat(paymentAmount) > selectedLayaway.remaining_amount && (
                  <p className="text-red-500 text-xs mt-1">Amount cannot exceed remaining balance</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Date</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="bg-white border-slate-200 focus:border-green-500"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Method</label>
                <Select value={paymentMethodDialog} onValueChange={setPaymentMethodDialog}>
                  <SelectTrigger className="bg-white border-slate-200 focus:border-green-500">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">
                      <div className="flex items-center space-x-2">
                        <Banknote className="h-4 w-4 text-green-600" />
                        <span>Cash</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="bank_transfer">
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        <span>Bank Transfer</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="credit_card">
                      <div className="flex items-center space-x-2">
                        <CreditCard className="h-4 w-4 text-purple-600" />
                        <span>Credit Card</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="debit_card">
                      <div className="flex items-center space-x-2">
                        <CreditCard className="h-4 w-4 text-indigo-600" />
                        <span>Debit Card</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Notes (Optional)</label>
                <Input
                  placeholder="Add payment notes..."
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  className="bg-white border-slate-200 focus:border-green-500"
                />
              </div>

              {/* Payment Preview */}
              {paymentAmount && selectedLayaway && parseFloat(paymentAmount) > 0 && parseFloat(paymentAmount) <= selectedLayaway.remaining_amount && (
                <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                  <div className="text-sm text-green-800">
                    <div className="font-medium mb-2">Payment Preview</div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>New Payment: {currencySymbol}{parseFloat(paymentAmount).toFixed(2)}</div>
                      <div>New Balance: {currencySymbol}{(selectedLayaway.remaining_amount - parseFloat(paymentAmount)).toFixed(2)}</div>
                      <div className="col-span-2 mt-1 pt-1 border-t border-green-300">
                        {selectedLayaway.remaining_amount - parseFloat(paymentAmount) <= 0 
                          ? <span className="font-medium text-green-700">🎉 Debtor will be completed!</span>
                          : <span className="text-green-700">Debtor will remain active</span>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={() => setShowPaymentDialog(false)}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleAddPayment}
                  disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || (selectedLayaway && parseFloat(paymentAmount) > selectedLayaway.remaining_amount) || isAddingPayment}
                  className="px-6 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  {isAddingPayment ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4" />
                      <span>Add Payment</span>
                    </div>
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog
          open={showEditPaymentDialog}
          onOpenChange={(open) => {
            setShowEditPaymentDialog(open);
            if (!open) {
              setEditingPayment(null);
            }
          }}
        >
          <DialogContent className="w-[95vw] max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Payment</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Amount</label>
                <Input
                  type="number"
                  value={editPaymentAmount}
                  onChange={(e) => setEditPaymentAmount(e.target.value)}
                  className="bg-white border-slate-200"
                  step="0.01"
                  min="0"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Date</label>
                <Input
                  type="date"
                  value={editPaymentDate}
                  onChange={(e) => setEditPaymentDate(e.target.value)}
                  className="bg-white border-slate-200"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Payment Method</label>
                <Select value={editPaymentMethod} onValueChange={setEditPaymentMethod}>
                  <SelectTrigger className="bg-white border-slate-200">
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="debit_card">Debit Card</SelectItem>
                    <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="transfer">Transfer</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">Notes</label>
                <Input
                  value={editPaymentNotes}
                  onChange={(e) => setEditPaymentNotes(e.target.value)}
                  placeholder="Payment notes"
                  className="bg-white border-slate-200"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditPaymentDialog(false);
                    setEditingPayment(null);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={handleUpdatePayment} disabled={isUpdatingPayment}>
                  {isUpdatingPayment ? 'Updating...' : 'Update Payment'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Invoice Dialog */}
        <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
          <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
            <DialogTitle>Debtor Invoice</DialogTitle>
            </DialogHeader>
            {selectedLayaway && (
              <LayawayInvoice
                layaway={selectedLayaway}
                currencySymbol={currencySymbol}
                vatPercentage={vatPercentage}
                branchName={branches.find(b => b.id === selectedLayaway.branch_id)?.name || ''}
                systemTimezone={systemTimezone}
                companyDetails={{
                  name: companySettings.company_name,
                  address: companySettings.address,
                  phone: companySettings.phone,
                  email: companySettings.email
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Debtor Dialog */}
        <EditLayawayDialog
          open={showEditDialog}
          onOpenChange={setShowEditDialog}
          layaway={editingLayaway}
          items={items}
          inventory={inventory}
          branches={branches}
          systemTimezone={systemTimezone}
          vatPercentage={vatPercentage}
          currencySymbol={currencySymbol}
          onSuccess={() => {
            fetchLayawaysData();
            fetchAllDebtorLayaways();
            setEditingLayaway(null);
          }}
        />
      </div>
    </div>
  );
};

export default Layaways;
